import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import {
  ArrowRight,
  Clock,
  DotsThreeVertical,
  DownloadSimple,
  Eye,
  FileText,
  Lightning,
  Lock,
  PencilSimple,
  Robot,
  Sparkle,
  Star,
  Target,
  Trash,
  TrendUp,
  UploadSimple
} from '@phosphor-icons/react';
/**
 * Polished Opportunity Card Component - Modern SaaS Design
 * Matches design patterns from Linear, Notion, Asana
 * 
 * TTFV Model Support:
 * - automation_status: 'ready' | 'coming_soon' | 'manual'
 * - Shows "Automation coming soon" badge when status is 'coming_soon'
 */
const OpportunityCard = ({
  id,
  title,
  description,
  one_liner,
  impact,
  effort,
  priority_score,
  roi,
  time_saved,
  status,
  category,
  source,
  linked_agent_id,
  linked_agent_name,
  iao,
  run_modal_variant,
  expected_outcomes,
  workflow_fit,
  example_inputs,
  example_outputs,
  is_favorite = false,
  locked = false, // Whether this opportunity is locked (requires upgrade)
  onUnlock = null, // Callback when user clicks unlock
  automation_status = null, // TTFV: 'ready' | 'coming_soon' | 'manual'
  opportunity_potential_label = null, // Scoring v2: 'Very High' | 'High' | 'Medium' | 'Developing'
  readiness_context_note = null, // Scoring v2: Context note for low readiness + high potential
  integrationCertainty = null, // V2.2: 'direct' | 'conditional' | 'pattern'
  deployment_note = null, // V2.2: Note explaining deployment requirements
  // MCP Spine Phase 1: Capability-first fields
  required_capabilities = [],
  optional_capabilities = [],
  suggested_output_types = [],
  integration_notes = null,
  // Phase 3: System linking fields
  match_type = null,
  suggested_system = null,
  suggested_system_id = null,
  onViewDetails,
  onToggleFavorite,
  index = 0,
  // Blueprint highlight props
  isHighlighted = false,
  autoExpand = false
}) => {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(autoExpand);
  // Use prop directly - parent component manages state
  const isFavorited = is_favorite;

  // Auto-scroll to highlighted card
  useEffect(() => {
    if (isHighlighted && cardRef.current) {
      setTimeout(() => {
        cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
  }, [isHighlighted]);

  // Normalize impact value (1-10)
  const getImpactValue = () => {
    if (typeof impact === 'number') return impact;
    if (typeof impact === 'string') {
      const match = impact.match(/(\d+)/);
      return match ? parseInt(match[1]) : 5;
    }
    return 5;
  };

  // Get effort display (1-10)
  const getEffortValue = () => {
    if (typeof effort === 'number') return effort;
    if (typeof effort === 'string') {
      const match = effort.match(/(\d+)/);
      return match ? parseInt(match[1]) : 5;
    }
    return 5;
  };
  
  // Get Opportunity Potential badge (Scoring v2)
  const getOpportunityPotentialBadge = () => {
    const label = opportunity_potential_label || (() => {
      // Calculate from impact/effort if not provided
      const impactVal = getImpactValue();
      const effortVal = getEffortValue();
      const potential = (impactVal * 10) / Math.max(1, effortVal);
      if (potential >= 15) return 'Very High';
      if (potential >= 10) return 'High';
      if (potential >= 6) return 'Medium';
      return 'Developing';
    })();
    
    const badges = {
      'Very High': { bg: 'bg-[#14F195]/20', text: 'text-[#14F195]' },
      'High': { bg: 'bg-[#00E5C0]/20', text: 'text-[#00E5C0]' },
      'Medium': { bg: 'bg-[#F59E0B]/20', text: 'text-[#F59E0B]' },
      'Developing': { bg: 'bg-[#718096]/20', text: 'text-[#718096]' }
    };
    
    return { ...badges[label], label };
  };

  // Get ROI badge styling
  const getROIBadge = () => {
    if (!roi || !roi.status) {
      return { bg: 'bg-[#4B5563]', text: 'text-[#9CA3AF]', label: 'Unknown ROI' };
    }
    const status = roi.status.toLowerCase();
    const percentage = roi.roi_percentage || 0;
    
    switch (status) {
      case 'excellent':
      case 'good':
      case 'estimated':  // Handle legacy 'Estimated' status
        return { 
          bg: 'bg-[#10B981]/20', 
          text: 'text-[#10B981]', 
          label: `Strong ROI +${percentage.toFixed(0)}%` 
        };
      case 'moderate':
        return { 
          bg: 'bg-[#F59E0B]/20', 
          text: 'text-[#F59E0B]', 
          label: `Moderate ROI +${percentage.toFixed(0)}%` 
        };
      case 'poor':
        return { 
          bg: 'bg-[#EF4444]/20', 
          text: 'text-[#EF4444]', 
          label: `Low ROI ${percentage.toFixed(0)}%` 
        };
      default:
        return { bg: 'bg-[#4B5563]', text: 'text-[#9CA3AF]', label: 'Unknown ROI' };
    }
  };

  // Get stage badge styling
  const getStageBadge = () => {
    const stage = status || 'identified';
    const stageLower = stage.toLowerCase();
    
    // Map all possible status values with proper colors
    switch (stageLower) {
      case 'identified':
        return { bg: 'bg-[#00D4B0]/20', text: 'text-[#00D4B0]', label: 'Identified' };
      case 'evaluating':
        return { bg: 'bg-[#3B82F6]/20', text: 'text-[#3B82F6]', label: 'Evaluating' };
      case 'planning':
        return { bg: 'bg-[#00E5C0]/20', text: 'text-[#00E5C0]', label: 'Planning' };
      case 'in_progress':
        return { bg: 'bg-[#F59E0B]/20', text: 'text-[#F59E0B]', label: 'In Progress' };
      case 'implemented':
        return { bg: 'bg-[#10B981]/20', text: 'text-[#10B981]', label: 'Implemented' };
      case 'paused':
        return { bg: 'bg-[#6B7280]/20', text: 'text-[#6B7280]', label: 'Paused' };
      case 'cancelled':
        return { bg: 'bg-[#EF4444]/20', text: 'text-[#EF4444]', label: 'Cancelled' };
      default:
        // Fallback for any unknown status - display it as-is
        return { bg: 'bg-[#00D4B0]/20', text: 'text-[#00D4B0]', label: stage.charAt(0).toUpperCase() + stage.slice(1) };
    }
  };

  // Get source badge
  const getSourceLabel = () => {
    if (source) {
      if (source.includes('org') || source.includes('role')) return 'Role-based';
      if (source.includes('process')) return 'Process-based';
      if (source.includes('job')) return 'Job Description';
    }
    return category || 'General';
  };

  // V2.2: Get integration certainty badge
  const getIntegrationCertaintyBadge = () => {
    if (!integrationCertainty) return null;
    
    switch (integrationCertainty) {
      case 'direct':
        return {
          bg: 'bg-[#14F195]/10',
          text: 'text-[#14F195]',
          border: 'border border-[#14F195]/30',
          label: 'Ready now',
          tooltip: 'One-click deployment - source and destination known'
        };
      case 'conditional':
        return {
          bg: 'bg-[#00E5C0]/10',
          text: 'text-[#00E5C0]',
          border: 'border border-[#00E5C0]/30',
          label: 'Choose systems',
          tooltip: 'Select source/destination during setup'
        };
      case 'pattern':
        return {
          bg: 'bg-[#F59E0B]/10',
          text: 'text-[#F59E0B]',
          border: 'border border-[#F59E0B]/30',
          label: 'Pattern',
          tooltip: 'Reusable automation pattern - select trigger + destination'
        };
      default:
        return null;
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `$${value.toLocaleString()}`;
  };

  const impactValue = getImpactValue();
  const effortValue = getEffortValue();
  const roiBadge = getROIBadge();
  const stageBadge = getStageBadge();
  const potentialBadge = getOpportunityPotentialBadge();
  const certaintybadge = getIntegrationCertaintyBadge(); // V2.2

  // Handle locked card click - either call onUnlock or do nothing
  const handleCardClick = () => {
    if (locked) {
      if (onUnlock) onUnlock();
      return;
    }
    if (onViewDetails) onViewDetails();
  };

  // ─── Outcome headline logic (same as detail page) ──────
  const getCardHeadline = () => {
    if (one_liner && one_liner.length > 15 && /save|improv|reduc|automat|faster|increas|boost|eliminat|streamlin/i.test(one_liner)) {
      return one_liner;
    }
    if (expected_outcomes?.length > 0) {
      const first = typeof expected_outcomes[0] === 'string' ? expected_outcomes[0] : '';
      if (first) return first.charAt(0).toUpperCase() + first.slice(1);
    }
    return title;
  };

  // ─── Setup time heuristic ──────
  const getCardSetupTime = () => {
    const num = typeof effort === 'number' ? effort : 5;
    if (num <= 3) return 'Instant';
    if (num <= 5) return '~5 min';
    if (num <= 7) return '~10 min';
    return '~15 min';
  };

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ delay: Math.min(index * 0.05, 0.3) }}
      layout
      className={`group rounded-xl p-4 transition-all duration-200 border min-h-[100px] relative ${
        locked
          ? 'cursor-pointer bg-[#11161D] border-[rgba(255,255,255,0.04)] hover:border-[rgba(99,102,241,0.15)]'
          : isHighlighted
            ? 'cursor-pointer bg-[#11161D] hover:bg-[#151B24] border-[rgba(0,229,192,0.2)] ring-1 ring-[rgba(0,229,192,0.15)]'
            : 'cursor-pointer bg-[#11161D] hover:bg-[#151B24] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.1)]'
      }`}
      onClick={handleCardClick}
      data-testid={`opportunity-card-${id}`}
    >
      {/* Top highlight badge */}
      {isHighlighted && (
        <div className="absolute -top-2.5 left-4 px-2.5 py-0.5 bg-[#00E5C0] text-[#0B0F14] text-[10px] font-bold rounded-full flex items-center gap-1">
          <Sparkle size={10} />
          Top Opportunity
        </div>
      )}

      {/* Locked overlay */}
      {locked && (
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0F1A]/80 rounded-xl z-10 pointer-events-none" />
      )}

      {/* Row 1: Headline + actions */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-[#F0F4F8] leading-snug line-clamp-2" data-testid="card-headline">
            {getCardHeadline()}
          </h3>
        </div>

        {/* Star + Menu */}
        <div className="relative flex-shrink-0 flex items-center gap-0.5">
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(id, !isFavorited); }}
            className={`p-1 rounded-md transition-all ${
              isFavorited ? 'text-[#F59E0B] bg-[rgba(245,158,11,0.1)]' : 'text-[#7D8896] hover:text-[#F59E0B] opacity-0 group-hover:opacity-100'
            }`}
          >
            <Star size={14} className={isFavorited ? 'fill-current' : ''} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
            className="p-1 hover:bg-[rgba(255,255,255,0.06)] rounded-md transition-colors opacity-0 group-hover:opacity-100"
          >
            <DotsThreeVertical size={14} className="text-[#7D8896]" />
          </button>

          {showMenu && (
            <div className="absolute right-0 top-7 z-10 bg-[#1A1F28] rounded-lg shadow-xl border border-[rgba(255,255,255,0.08)] py-1 min-w-[130px]">
              <button onClick={(e) => { e.stopPropagation(); setShowMenu(false); onViewDetails(); }} className="w-full px-3 py-1.5 text-left text-xs text-[#F0F4F8] hover:bg-[rgba(255,255,255,0.06)] flex items-center gap-2">
                <Eye size={12} /> View Details
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Short benefit line */}
      <p className="text-xs text-[#B0B8C4] mb-3 line-clamp-2 leading-relaxed" data-testid="card-benefit">
        {description && typeof description === 'string' ? (description.length > 120 ? description.slice(0, 120) + '…' : description) : ''}
      </p>

      {/* Row 3: ROI condensed + Setup time + Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* ROI */}
          {!locked && roi && (
            <div className="flex items-center gap-1.5" data-testid="card-roi">
              <TrendUp size={12} className="text-[#00E5C0]" />
              <span className="text-[11px] font-semibold text-[#00E5C0]">
                {roi.roi_percentage !== undefined && roi.roi_percentage !== null
                  ? `${roi.roi_percentage.toFixed(0)}% ROI`
                  : roiBadge.label}
              </span>
              {(roi.annual_savings ?? roi.roi_annual) !== undefined && (
                <span className="text-[10px] text-[#7D8896]">
                  ({formatCurrency(roi.annual_savings ?? roi.roi_annual)}/yr)
                </span>
              )}
            </div>
          )}
          {locked && (
            <span className="text-[10px] text-[#7D8896]">Upgrade to see ROI</span>
          )}

          {/* Setup time */}
          {!locked && (
            <div className="flex items-center gap-1 text-[10px] text-[#7D8896]" data-testid="card-setup-time">
              <Clock size={10} />
              {getCardSetupTime()}
            </div>
          )}
        </div>

        {/* CTA */}
        {locked ? (
          <button
            onClick={(e) => { e.stopPropagation(); onUnlock?.(); }}
            className="text-[11px] font-semibold text-[#818CF8] hover:text-white flex items-center gap-1 px-2.5 py-1 rounded-md bg-[rgba(99,102,241,0.08)] hover:bg-[rgba(99,102,241,0.15)] transition-colors"
          >
            <Lock size={10} /> Unlock
          </button>
        ) : (
          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            {linked_agent_id ? (
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/partner-pro/ai-workforce/agents/${linked_agent_id}/edit`); }}
                className="text-[10px] font-semibold text-[#00E5C0] hover:text-white flex items-center gap-1 px-2 py-1 rounded-md bg-[rgba(0,229,192,0.06)] hover:bg-[rgba(0,229,192,0.12)] transition-colors"
                data-testid="go-to-worker-btn"
              >
                <Robot size={10} /> Open Worker
              </button>
            ) : match_type === 'deterministic' && suggested_system ? (
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/opportunities/${id}`); }}
                className="text-[10px] font-semibold text-[#00E5C0] hover:text-white flex items-center gap-1 px-2 py-1 rounded-md bg-[rgba(0,229,192,0.06)] hover:bg-[rgba(0,229,192,0.12)] transition-colors"
                data-testid="system-match-card-btn"
              >
                <Lightning size={10} weight="fill" /> {suggested_system.name?.length > 20 ? 'Set Up System' : `Set Up ${suggested_system.name}`}
              </button>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/partner-pro/ai-workforce/agents/create-from-opportunity/${id}`); }}
                className="text-[10px] font-semibold text-[#00E5C0] hover:text-white flex items-center gap-1 px-2 py-1 rounded-md bg-[rgba(0,229,192,0.06)] hover:bg-[rgba(0,229,192,0.12)] transition-colors"
                data-testid="setup-worker-card-btn"
              >
                <Sparkle size={10} /> Set up worker
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onViewDetails?.(); }}
              className="text-[10px] font-medium text-[#B0B8C4] hover:text-white flex items-center gap-1"
            >
              View <ArrowRight size={10} />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default OpportunityCard;
